if (!customElements.get('custom-bundle-collection')) {
  customElements.define(
    'custom-bundle-collection',
    class BundleCollection extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          bundleTotalPrice: ['[data-total-price]'],
          productItem: ['.product-bundle__product'],
          btnAddBundle: '[data-add-bundle]',
          variantPickers: ['[name="id"]'],
          productVariants: ['[data-product-variants]'],
          errorWrapper: '.product-bundle__error',
        };
        this.domNodes = queryDomNodes(this.selectors, this);
        this.cartDrawer = document.querySelector('cart-drawer');
        this.mainCart = document.querySelector('cart-items');
        this.Notification = window.FoxTheme.Notification;
      }

      connectedCallback() {
        this.init();
        this._initCollectionPicker();
      }

      init() {
        this.productVariants = {};

        // Standard product variants (slots 1, 3, 4, 5)
        this.querySelectorAll('[data-product-variants]').forEach((el) => {
          const productId = el.dataset.productId;
          const json = el.innerHTML ? JSON.parse(el.innerHTML) : [];
          this.productVariants[productId] = json;
        });

        // Collection slot variants: map of { "productId": variantsArray, ... }
        const collVariantsEl = this.querySelector('[data-collection-variants]');
        if (collVariantsEl) {
          try {
            this._collectionVariantsMap = JSON.parse(collVariantsEl.innerHTML);
          } catch (e) {
            this._collectionVariantsMap = {};
          }
          const collSlot = this.querySelector('[data-collection-slot]');
          if (collSlot) {
            const firstProductId = collSlot.dataset.currentProductId;
            if (firstProductId && this._collectionVariantsMap[firstProductId]) {
              this.productVariants['collection-slot'] = this._collectionVariantsMap[firstProductId];
            }
          }
        }

        // All change/click listeners are bound to THIS element — fully scoped,
        // never leaks into other sections on the same page.
        this.addEventListener('change', (e) => {
          if (e.target.matches('[name="id"]')) this._handleChangePrice(e);
          if (e.target.matches('[data-quantity-input]')) this._calculateTotalPrice();
        });

        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-add-bundle]');
          if (btn && this.contains(btn)) this._handleAddItems(e, btn);
        });

        this._calculateTotalPrice();
      }

      /**
       * Wire up the collection dropdown (slot 2).
       * On change: update image, title link, hidden variant input,
       * productVariants cache, price display, and reset qty to 4.
       */
      _initCollectionPicker() {
        const collSlot = this.querySelector('[data-collection-slot]');
        if (!collSlot) return;
        const picker = collSlot.querySelector('.product-bundle__collection-picker');
        if (!picker) return;

        picker.addEventListener('change', (e) => {
          // Ignore programmatic events dispatched by theme JS during init
          if (!e.isTrusted) return;
          const opt = e.target.selectedOptions[0];
          if (!opt) return;

          const variantId = opt.dataset.variantId;
          const productId  = opt.value;                          // numeric product ID string
          const price      = parseInt(opt.dataset.price, 10) || 0;
          const compare    = parseInt(opt.dataset.comparePrice, 10) || 0;
          const imageUrl   = opt.dataset.image || '';
          const url        = opt.dataset.url || '';
          const title      = opt.textContent.trim();

          // 1. Update hidden variant input so _handleAddItems picks it up
          const hiddenInput = collSlot.querySelector('[data-collection-variant-input]');
          if (hiddenInput) hiddenInput.value = variantId;

          // 2. Swap productVariants cache for this slot
          if (this._collectionVariantsMap && this._collectionVariantsMap[productId]) {
            this.productVariants['collection-slot'] = this._collectionVariantsMap[productId];
          }

          // 3. Update product links (image wrapper + optional title link)
          collSlot.querySelectorAll('[data-collection-title-link], [data-collection-title-link-wrap]').forEach((el) => {
            if (url) el.href = url;
            if (el.matches('[data-collection-title-link]')) el.textContent = title;
            const hidden = el.querySelector('.visually-hidden');
            if (hidden) hidden.textContent = title;
          });

          // 4. Swap image
          const imgEl = collSlot.querySelector('[data-collection-image]');
          if (imgEl && imageUrl) {
            imgEl.src = imageUrl;
            imgEl.srcset = '';
            imgEl.alt = title;
          }

          // 5. Reset quantity to 4
          const qtyInput = collSlot.querySelector('[data-quantity-input]');
          if (qtyInput) qtyInput.value = 4;

          // 6. Update price display (qty = 4 for chairs)
          const qty = 4;
          const priceWrapper  = collSlot.querySelector('[data-price]');
          const regularPriceEl = collSlot.querySelector('[data-regular-price]');
          const comparePriceEl = collSlot.querySelector('[data-compare-price]');

          if (regularPriceEl) {
            regularPriceEl.innerHTML = formatMoney(price * qty, window.FoxThemeSettings.money_format);
            regularPriceEl.dataset.price = price;
          }
          if (compare > price) {
            if (priceWrapper) priceWrapper.classList.add('price--on-sale');
            if (comparePriceEl) comparePriceEl.innerHTML = formatMoney(compare * qty, window.FoxThemeSettings.money_format);
          } else {
            if (priceWrapper) priceWrapper.classList.remove('price--on-sale');
            if (comparePriceEl) comparePriceEl.innerHTML = '';
          }

          this._calculateTotalPrice();
        });
      }

      _handleChangePrice(evt) {
        const product = evt.target && evt.target.closest(this.selectors.productItem[0]);
        if (!product) return;
        const selectedVariant = evt.target && evt.target.value;
        const productId = product.dataset.productId;
        const variantsJson = this.productVariants[productId];

        const priceWrapper  = product.querySelector('[data-price]');
        const regularPrice  = product.querySelector('[data-regular-price]');
        const comparePrice  = product.querySelector('[data-compare-price]');

        const variant = variantsJson && variantsJson.find((v) => v.id === parseInt(selectedVariant));
        if (!variant) return;

        const qtyInput = product.querySelector('[data-quantity-input]');
        const quantity = qtyInput ? Math.max(parseInt(qtyInput.value) || 1, 1) : 1;

        regularPrice.innerHTML = formatMoney(variant.price * quantity, window.FoxThemeSettings.money_format);
        regularPrice.dataset.price = variant.price;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceWrapper.classList.add('price--on-sale');
          comparePrice.innerHTML = formatMoney(variant.compare_at_price * quantity, window.FoxThemeSettings.money_format);
        } else {
          priceWrapper.classList.remove('price--on-sale');
        }

        this._calculateTotalPrice();
      }

      _calculateTotalPrice() {
        const productItems = Array.from(this.querySelectorAll(this.selectors.productItem[0]));

        const selectedVariants = productItems
          .map((product) => {
            const productId = product.dataset.productId;
            const variants  = this.productVariants[productId];
            // Both regular selects and the collection hidden input share name="id"
            const select    = product.querySelector('[name="id"]');
            const qtyInput  = product.querySelector('[data-quantity-input]');
            const quantity  = qtyInput ? Math.max(parseInt(qtyInput.value) || 1, 1) : 1;
            const variant   = variants && select && variants.find((v) => v.id === Number(select.value));
            if (!variant) return null;

            const regularPrice  = product.querySelector('[data-regular-price]');
            const comparePrice  = product.querySelector('[data-compare-price]');
            const priceWrapper  = product.querySelector('[data-price]');
            if (regularPrice) {
              regularPrice.innerHTML = formatMoney(variant.price * quantity, window.FoxThemeSettings.money_format);
            }
            if (comparePrice && variant.compare_at_price && variant.compare_at_price > variant.price) {
              if (priceWrapper) priceWrapper.classList.add('price--on-sale');
              comparePrice.innerHTML = formatMoney(variant.compare_at_price * quantity, window.FoxThemeSettings.money_format);
            } else if (priceWrapper) {
              priceWrapper.classList.remove('price--on-sale');
            }

            return { ...variant, quantity };
          })
          .filter(Boolean);

        const totalPrice = selectedVariants.reduce((s, v) => s + v.price * v.quantity, 0);
        const savedPrice = selectedVariants.reduce((s, v) => {
          return s + (v.compare_at_price ? v.compare_at_price * v.quantity : v.price * v.quantity);
        }, 0);

        const totalEl = this.querySelector('[data-total-price]');
        const savedEl = this.querySelector('[data-saved-price]');
        if (totalEl) totalEl.innerHTML = formatMoney(totalPrice, window.FoxThemeSettings.money_format);
        if (savedEl)  savedEl.innerHTML  = formatMoney(savedPrice, window.FoxThemeSettings.money_format);
      }

      _handleAddItems(e, button) {
        e.preventDefault();
        const { FoxThemeStrings } = window;
        const errorWrapper = this.querySelector(this.selectors.errorWrapper);

        // querySelectorAll('[name="id"]') returns both regular selects AND the
        // collection hidden input, so no special-casing needed here.
        const inputIds = this.querySelectorAll('[name="id"]');
        const data = {
          items: [...inputIds].map((input) => {
            const productItem = input.closest(this.selectors.productItem[0]);
            const qtyInput = productItem && productItem.querySelector('[data-quantity-input]');
            const quantity = qtyInput ? Math.max(parseInt(qtyInput.value) || 1, 1) : 1;
            return { id: input.value, quantity };
          }),
        };

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
        };

        let sectionToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionToBundle },
          })
        );

        if (this.cartDrawer) {
          sectionToBundle = [...sectionToBundle, ...this.cartDrawer.getSectionsToRender().map((s) => s.id)];
        }
        if (this.mainCart) {
          sectionToBundle = [...sectionToBundle, ...this.mainCart.getSectionsToRender().map((s) => s.section)];
        }

        config.body = JSON.stringify({
          ...data,
          sections: sectionToBundle,
          sections_url: window.location.pathname,
        });

        this._toggleLoading(true, button);
        fetch(FoxThemeSettings.routes.cart_add_url, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this._showError(response.description || 'Failed to add all items to cart!', errorWrapper);
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', { detail: { errorMessage: response.description } })
              );
            } else {
              if (!FoxThemeSettings.cart.openDrawerWhenAdded || FoxThemeSettings.cart.cartType === 'page') {
                this.Notification.show({
                  target: this.domNodes.errorWrapper || document.body,
                  method: 'appendChild',
                  type: 'success',
                  message: FoxThemeStrings.item_added,
                  last: 3000,
                });
              }
              if (FoxThemeSettings.cart.cartType === 'page') {
                FoxThemeCartHelpers.getState().then((state) => {
                  FoxThemeCartHelpers.updateCartCount(state.item_count);
                });
              }
              FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);
              document.dispatchEvent(
                new CustomEvent('product-ajax:added', { detail: { product: response } })
              );
            }
          })
          .catch((err) => this._showError(err && err.toString(), errorWrapper))
          .finally(() => this._toggleLoading(false, button));
      }

      _showError(err, errorWrapper) {
        FoxTheme.Notification.show({
          target: errorWrapper,
          method: 'appendChild',
          type: 'warning',
          message: err,
          last: 3000,
        });
      }

      _toggleLoading(loading, button) {
        if (loading) button.classList.add('btn--loading');
        else button.classList.remove('btn--loading');
      }
    }
  );
}